import React, { useState } from 'react';
import './StrategyList.css';

function StrategyList({ strategies, onEdit, onDelete, onToggleActive, loading = false }) {
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDelete = (id) => {
    setConfirmDelete(id);
  };

  const confirmDeleteAction = (id) => {
    onDelete(id);
    setConfirmDelete(null);
  };

  if (strategies.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📊</div>
        <h3>No Strategies Yet</h3>
        <p>Start by creating your first trading strategy with custom indicators and conditions.</p>
      </div>
    );
  }

  return (
    <div className="strategy-list">
      <table className="strategies-table">
        <thead>
          <tr>
            <th>Strategy Name</th>
            <th>Status</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {strategies.map((strategy) => (
            <React.Fragment key={strategy.id}>
              <tr className={`strategy-row ${strategy.is_active ? 'active' : 'inactive'}`}>
                <td className="strategy-name">
                  <div
                    className="expand-toggle"
                    onClick={() =>
                      setExpandedId(
                        expandedId === strategy.id ? null : strategy.id
                      )
                    }
                  >
                    <span className="expand-icon">
                      {expandedId === strategy.id ? '▼' : '▶'}
                    </span>
                    {strategy.strategy_name}
                  </div>
                </td>
                <td>
                  <span
                    className={`status-badge ${
                      strategy.is_active ? 'active' : 'inactive'
                    }`}
                  >
                    {strategy.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="date">{formatDate(strategy.created_at)}</td>
                <td className="date">{formatDate(strategy.updated_at)}</td>
                <td className="actions">
                  {confirmDelete === strategy.id ? (
                    <div className="delete-confirm-inline">
                      <span>Delete?</span>
                      <button
                        className="btn-icon btn-confirm-yes"
                        title="Confirm delete"
                        onClick={() => confirmDeleteAction(strategy.id)}
                      >
                        ✓
                      </button>
                      <button
                        className="btn-icon btn-confirm-no"
                        title="Cancel"
                        onClick={() => setConfirmDelete(null)}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        className="btn-icon btn-toggle"
                        title={
                          strategy.is_active
                            ? 'Deactivate strategy'
                            : 'Activate strategy'
                        }
                        onClick={() =>
                          onToggleActive(strategy.id, !strategy.is_active)
                        }
                        disabled={loading}
                      >
                        {strategy.is_active ? '⏸' : '▶'}
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        title="Edit strategy"
                        onClick={() => onEdit(strategy)}
                        disabled={loading}
                      >
                        ✎
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        title="Delete strategy"
                        onClick={() => handleDelete(strategy.id)}
                        disabled={loading}
                      >
                        🗑
                      </button>
                    </>
                  )}
                </td>
              </tr>

              {/* Expanded Details Row */}
              {expandedId === strategy.id && (
                <tr className="expansion-row">
                  <td colSpan="5">
                    <div className="strategy-details">
                      <div className="details-section">
                        <h4>Indicators</h4>
                        <div className="indicators-display">
                          {strategy.config?.indicators ? (
                            Object.entries(strategy.config.indicators).map(
                              ([name, settings]) => (
                                <div
                                  key={name}
                                  className={`indicator-badge ${
                                    settings.enabled ? 'enabled' : 'disabled'
                                  }`}
                                >
                                  {name}
                                </div>
                              )
                            )
                          ) : (
                            <p>No indicators configured</p>
                          )}
                        </div>
                      </div>

                      <div className="details-section">
                        <h4>Risk Management</h4>
                        <div className="risk-display">
                          {strategy.config?.riskManagement ? (
                            <ul>
                              <li>
                                Stop Loss:{' '}
                                <strong>
                                  {strategy.config.riskManagement.stopLoss}%
                                </strong>
                              </li>
                              <li>
                                Take Profit:{' '}
                                <strong>
                                  {
                                    strategy.config.riskManagement.takeProfit
                                  }%
                                </strong>
                              </li>
                              <li>
                                Max Position:{' '}
                                <strong>
                                  ${strategy.config.riskManagement.maxPosition}
                                </strong>
                              </li>
                            </ul>
                          ) : (
                            <p>No risk management configured</p>
                          )}
                        </div>
                      </div>

                      {confirmDelete === strategy.id && (
                        <div className="delete-confirm">
                          <p>Are you sure you want to delete this strategy?</p>
                          <div className="confirm-actions">
                            <button
                              className="btn-confirm btn-danger"
                              onClick={() => confirmDeleteAction(strategy.id)}
                            >
                              Delete
                            </button>
                            <button
                              className="btn-confirm btn-cancel"
                              onClick={() => setConfirmDelete(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default StrategyList;
